import { motion, useInView } from "framer-motion"
import { Link, graphql } from "gatsby"
import parse from "html-react-parser"
import React, { useEffect, useRef, useState } from "react"
import useScript from "../Hooks/useScript"
import ImageOpt from "./ImageOpt"

const IntroductionContent = ({ module, src, alt, speed = 0.5 }) => {
  const ref = useRef(null)
  const [hasPlayed, setHasPlayed] = useState(false)
  const isInView = useInView(ref)

  const sectionShow = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        delayChildren: 0.5,
        staggerChildren: 0.2,
      },
    },
  }
  const animFade = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeInOut",
        delayChildren: 0.5,
        staggerChildren: 0.2,
      },
    },
  }

  useEffect(() => {
    if (isInView && !hasPlayed) {
      setHasPlayed(true)
    }
  }, [isInView, hasPlayed, setHasPlayed])

  const scrollRef = useRef(null)

  const jarallaxRefs = useRef([])

  const jarallaxLoaded = useScript(
    "https://cdn.jsdelivr.net/npm/jarallax@2/dist/jarallax.min.js"
  )
  const jarallaxVideoLoaded = useScript(
    "https://cdn.jsdelivr.net/npm/jarallax@2/dist/jarallax-video.min.js"
  )
  useEffect(() => {
    if (jarallaxLoaded && jarallaxVideoLoaded) {
      jarallaxRefs.current.forEach(ref => {
        if (ref) {
          window.jarallax(ref, { speed })
        }
      })
    }
  }, [jarallaxLoaded, jarallaxVideoLoaded, speed])

  const customStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      padding: "0",
      border: "0",
      borderRadius: "0",
      width: "100%",
      height: "100%",
      zIndex: "99999999",
      backgroundColor: "black",
    },
    overlay: {
      zIndex: "99999999",
    },
  }
  if (module.hideSection === "no") {
    return (
      <React.Fragment>
        <motion.section
          initial="hidden"
          animate={isInView || hasPlayed ? "show" : "hidden"}
          variants={sectionShow}
          className={`intro py-60 xlscreen:py-45 mdscreen:py-35 ${
            module.extraClass ? ` ${module.extraClass}` : ""
          }`}
          id={module.extraId}
        >
          <motion.div
            ref={ref}
            initial="hidden"
            animate={isInView || hasPlayed ? "show" : "hidden"}
            variants={sectionShow}
            className="container-fluid-md"
          >
            <div className="flex flex-wrap lgscreen:justify-between gap-y-25">
              {module?.imageOne && (
                <div className="flex-item w-3/12 lgscreen:w-5/12 mdscreen:w-full lgscreen:order-1">
                  <div className="border-bg-offwhite">
                    <div
                      className="h-[290px] relative overflow-hidden jarallax cross"
                      ref={el => (jarallaxRefs.current[0] = el)}
                      data-jarallax
                      data-speed="0.9"
                    >
                      <img
                        src={module.imageOne.mediaItemUrl}
                        alt={module.imageOne.title}
                        width={300}
                        height={290}
                        loading="lazy"
                        className="jarallax-img"
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="flex-item w-6/12 pt-85 lgscreen:pt-50 lgscreen:w-full lgscreen:order-3 lgscreen:!pr-0">
                <div className="items_wrapper text-center">
                  <motion.div variants={animFade} className="ring-group">
                    <div className="ring">
                      <img
                        src="../../images/spiral-pattern.svg"
                        alt="ring-icon"
                        height={46}
                        width={300}
                        loading="lazy"
                      />
                    </div>
                  </motion.div>
                  {module?.heading && (
                    <div className="title title-grey pt-20 uppercase text-center">
                      <motion.h2 variants={animFade}>
                        {module.heading}
                      </motion.h2>
                    </div>
                  )}
                  {module?.description && (
                    <motion.div
                      variants={animFade}
                      className="content max-w-[500px] mx-auto pt-15"
                    >
                      <p>{parse(module.description)}</p>
                    </motion.div>
                  )}
                  {module?.button && (
                    <motion.div
                      variants={animFade}
                      className="pt-30 mdscreen:pt-20"
                    >
                      <Link
                        to={module.button.url}
                        className="btn btn-grey"
                        target={module.button.target}
                      >
                        {module.button.title}
                      </Link>
                    </motion.div>
                  )}
                </div>
              </div>
              {module?.imageTwo && (
                <div className="flex-item w-3/12 lgscreen:w-5/12 mdscreen:w-full lgscreen:order-2">
                  <div className="border-bg-offwhite">
                    <div
                      className="h-[396px] relative overflow-hidden jarallax cross"
                      data-jarallax
                      data-speed="0.9"
                      ref={el => (jarallaxRefs.current[1] = el)}
                    >
                      <img
                        src={module.imageTwo.mediaItemUrl}
                        alt={module.imageTwo.title}
                        width={335}
                        height={396}
                        loading="lazy"
                        className="jarallax-img"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-wrap justify-between pr-75 lgscreen:pt-50 lgscreen:pr-0 gap-y-25">
              {module?.imageThree && (
                <div className="w-4/12 lgscreen:w-5/12 mdscreen:w-full lgscreen:pr-25 mdscreen:pr-0">
                  <div className="border-bg-offwhite">
                    <div
                      className="h-[340px] relative overflow-hidden jarallax cross"
                      data-jarallax
                      data-speed="0.9"
                      ref={el => (jarallaxRefs.current[2] = el)}
                    >
                      <img
                        src={module.imageThree.mediaItemUrl}
                        alt={module.imageThree.title}
                        width={447}
                        height={340}
                        loading="lazy"
                        className="jarallax-img"
                      />
                    </div>
                  </div>
                </div>
              )}
              {module?.imageFour && (
                <div className="w-4/12 pt-85 lgscreen:pt-50 lgscreen:w-5/12 mdscreen:w-full mdscreen:pt-0">
                  <div className="border-bg-offwhite">
                    <div className="overflow-hidden">
                      <div
                        className="h-[270px] relative overflow-hidden jarallax cross"
                        data-jarallax
                        data-speed="0.9"
                        ref={el => (jarallaxRefs.current[3] = el)}
                      >
                        <img
                          src={module.imageFour.mediaItemUrl}
                          alt={module.imageFour.title}
                          width={454}
                          height={270}
                          loading="lazy"
                          className="jarallax-img"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.section>
      </React.Fragment>
    )
  }
}

export default IntroductionContent
export const PageIntroductionContentFragment = graphql`
  fragment PageIntroductionContentFragment on WpPage_Pagecontent_PageContent_IntroductionContent {
    extraClass
    extraId
    hideSection
    description
    button {
      target
      title
      url
    }
    heading
    imageOne {
      altText
      mediaItemUrl
      title
    }
    imageTwo {
      altText
      mediaItemUrl
      title
    }
    imageThree {
      altText
      mediaItemUrl
      title
    }
    imageFour {
      altText
      mediaItemUrl
      title
    }
  }
`
